import React, { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useMergedState } from '../../hooks/useMergedState';
import validate from 'validate.js';
import RegisterContext from './RegisterContext';
import { AlertType } from '../components/Alert';
import { IBaseForm } from '../../types/types';
import { registerFormConstraints } from '../../constraints/RegisterForm';
import { UseTranslate } from '../../hooks/Translate';
import CasCalendar from '../components/CasCalendar';
import { RangeKeyDict } from 'react-date-range';

interface IRegisterFormState extends IBaseForm {
  focus: Record<string, boolean>;
}

enum Citizenship {
  GEORGIA = 1,
  ABROAD = 2,
}

export const RegisterForm = (props: any) => {
  const [state, setState] = useMergedState<IRegisterFormState>({
    focus: {},
    errors: {},
    validated: false,
  });

  const { setAlertProps, toggleModal } = useContext(RegisterContext);
  const [translate] = UseTranslate();

  const [dateSelect, setDateSelect] = useState<any>(null);
  const [birthDateError, setBirthDateError] = useState(false);
  const [birthDateRequired, setBirthDateRequired] = useState(false);
  const [gender, setGender] = useState<number>();
  const [vehicleType, setVehicleType] = useState<number>(1);
  const [citizenStatus, setCitizenStatus] = useState<number>(1);
  const [citizenship, setCitizenship] = useState<number>(Citizenship.GEORGIA);

  const handleCitizenshipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = +e.target.value;
    setCitizenship(v);
    if (v === Citizenship.GEORGIA) {
      onPidChange(v);
    } else {
      delete state.errors.pid;
      setState(state);
    }
  };

  useEffect(() => {
    function subtractYears(numOfYears: number, date = new Date()) {
      date.setFullYear(date.getFullYear() - numOfYears);
      return date;
    }
    const validBirthDate = subtractYears(18);
    setBirthDateRequired(false);
    if (dateSelect && dateSelect >= validBirthDate) {
      setBirthDateError(true);
    } else {
      setBirthDateError(false);
    }
  }, [dateSelect]);

  const onChangePassword = () => {
    const { result, elements } = validatePassword();

    setState({
      errors: {
        ...state.errors,
        ...result,
      },
    });

    for (const key in elements) {
      if (result && typeof result[key] === 'object') {
        elements[key].classList.add('is-invalid');
      } else {
        elements[key].classList.remove('is-invalid');
      }
    }
  };

  const validatePassword = () => {
    const elements: { [key: string]: HTMLInputElement } = {};
    const attributes: { [key: string]: string } = {};
    const _constraints: { [key: string]: object } = {};

    for (const key of ['password', 'repeatPassword']) {
      const selector = '[name="' + key + '"]';
      const element = document.querySelector(selector) as HTMLInputElement;
      if (state.focus[key]) {
        elements[key] = element;
        attributes[key] = element.value;
        _constraints[key] = registerFormConstraints(
          translate('cas.registration.pswdMin8Char'),
          translate('cas.registration.pswdDontMatch'),
          translate('cas.registration.registerAcceptRules'),
          translate('cas.registration.registerPidFormat'),
          citizenship === Citizenship.GEORGIA
            ? new RegExp('\\d{11}$')
            : new RegExp('[a-zA-Z0-9]+')
        )[key];
      }
    }

    const result = validate(attributes, _constraints, { fullMessages: false });

    return {
      elements,
      attributes,
      result,
    };
  };

  const onPidChange = (citizenship: Citizenship) => {
    const { result, elements } = validatePid(citizenship);

    setState({
      errors: {
        ...state.errors,
        pid: result?.pid,
      },
    });
    if (typeof result?.pid === 'object') {
      elements.pid.classList.add('is-invalid');
    } else {
      elements.pid.classList.remove('is-invalid');
    }
  };
  const validatePid = (citizenship: Citizenship) => {
    const elements: { [key: string]: HTMLInputElement } = {};
    const attributes: { [key: string]: string } = {};
    const _constraints: { [key: string]: object } = {};

    for (const key of ['pid']) {
      const selector = '[name="' + key + '"]';
      const element = document.querySelector(selector) as HTMLInputElement;
      if (state.focus[key]) {
        elements[key] = element;
        attributes[key] = element.value;
        _constraints[key] = registerFormConstraints(
          translate('cas.registration.pswdMin6Char'),
          translate('cas.registration.pswdDontMatch'),
          translate('cas.registration.registerAcceptRules'),
          translate('cas.registration.registerPidFormat'),
          citizenship === Citizenship.GEORGIA
            ? new RegExp('\\d{11}$')
            : new RegExp('[a-zA-Z0-9]+')
        )[key];
      }
    }

    const result = validate(attributes, _constraints, { fullMessages: false });
    return {
      elements,
      attributes,
      result,
    };
  };
  const onChangeRepeatPassword = () => {
    const { result, elements } = validatePassword();

    setState({
      errors: {
        ...state.errors,
        repeatPassword: result?.repeatPassword,
      },
    });

    if (typeof result?.repeatPassword === 'object') {
      elements.repeatPassword.classList.add('is-invalid');
    } else {
      elements.repeatPassword.classList.remove('is-invalid');
    }
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setState({
      focus: {
        ...state.focus,
        [e.target.name]: true,
      },
    });
  };

  const togglePassword = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.currentTarget.classList.toggle('toggle-visible-password--visible');
    const previousSibling = e.currentTarget.previousSibling as HTMLInputElement;
    const type = previousSibling.getAttribute('type');
    switch (type) {
      case 'text':
        previousSibling.setAttribute('type', 'password');
        break;
      case 'password':
        previousSibling.setAttribute('type', 'text');
        break;
    }
  };

  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();

    const fd = new FormData(e.target as HTMLFormElement);
    const values = Object.fromEntries<any>(fd.entries());

    const result = validate(
      values,
      registerFormConstraints(
        translate('cas.registration.pswdMin6Char'),
        translate('cas.registration.pswdDontMatch'),
        translate('cas.registration.registerAcceptRules'),
        translate('cas.registration.registerPidFormat'),
        citizenship === Citizenship.GEORGIA
          ? new RegExp('\\d{11}$')
          : new RegExp('[a-zA-Z0-9]+')
      ),
      { fullMessages: false }
    );

    if (result || !dateSelect || vehicleType === -1 || citizenStatus === -1) {
      const state = { errors: result || {} };
      if (vehicleType === -1)
        state.errors['vehicleType'] = ['cas.registration.requiredFields'];
      if (citizenStatus === -1)
        state.errors['citizenStatus'] = ['cas.registration.requiredFields'];
      setState(state);

      setAlertProps({
        type: AlertType.ERROR,
        title: translate('cas.registration.registerError'),
        message: translate('cas.registration.registerCheckErrorFields'),
      });
      if (!dateSelect) {
        setBirthDateRequired(true);
      }
    } else {
      setState({ errors: {} });
      values.agreement = true;
      values.gender = gender === -1 ? null : gender;
      values.birthDate = dateSelect;
      props.register(values);
    }

    setState({ validated: true });
  };

  const onDateConfirm = (value: RangeKeyDict | Date) => {
    if (value instanceof Date === true) {
      setDateSelect(value);
    }
  };

  return (
    <Form validated={state.validated} onSubmit={onFormSubmit} noValidate>
      <Form.Group className="mb-3" controlId="firstName">
        <Form.Label className="small user-icon">
          {translate('cas.registration.FirstName')}
        </Form.Label>
        <Form.Control
          type="text"
          name="firstName"
          placeholder={translate('cas.registration.inputFirstName')}
          autoComplete="firstName"
          isInvalid={!!state.errors?.firstName}
          // onChange={(e) => onChange(e, "firstName")}
          required
        />
        <Form.Control.Feedback type="invalid">
          {state.errors && state.errors.firstName && state.errors.firstName[0]}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3" controlId="lastName">
        <Form.Label className="small user-icon">
          {translate('cas.registration.LastName')}
        </Form.Label>
        <Form.Control
          type="text"
          name="lastName"
          placeholder={translate('cas.registration.inputLasttName')}
          autoComplete="lastName"
          isInvalid={!!state.errors?.lastName}
          // onChange={(e) => onChange(e, "lastName")}
          required
        />
        <Form.Control.Feedback type="invalid">
          {state.errors && state.errors.lastName && state.errors.lastName[0]}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3" controlId="citizenship">
        <Form.Label className="small">
          {translate('cas.registration.citizenship')}
        </Form.Label>
        <div className="primary-radio">
          <Form.Check
            inline={true}
            type="radio"
            name="citizenship"
            value={1}
            id="georgia"
            label={translate('cas.registration.georgia')}
            onChange={handleCitizenshipChange}
            defaultChecked
          />
          <Form.Check
            inline={true}
            type="radio"
            name="citizenship"
            value={2}
            label={translate('cas.registration.abroad')}
            id="abroad"
            onChange={handleCitizenshipChange}
          />
        </div>
      </Form.Group>

      <Form.Group className="mb-3" controlId="pid">
        <Form.Label className="small user-icon">
          {translate('cas.registration.pid')}
        </Form.Label>
        <Form.Control
          type="text"
          name="pid"
          placeholder={translate('cas.registration.inputpid')}
          autoComplete="pid"
          isInvalid={!!state.errors?.pid}
          required
          onFocus={onFocus}
          onChange={() => onPidChange(citizenship)}
          onInput={(e) => {
            e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');
          }}
        />
        <Form.Control.Feedback type="invalid">
          {state.errors && state.errors.pid && state.errors.pid[0]}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="reg-gender-form-group mb-3" controlId="gender">
        <Form.Label className="small gender-icon">
          {' '}
          {translate('cas.profile.gender')}
        </Form.Label>
        <Form.Select
          name="gender"
          className="custom-dropdown-register"
          onChange={(e) => {
            setGender(Number.parseInt(e.target.value));
          }}
        >
          <option value="-1" selected={!gender || gender === -1}>
            {' '}
            {translate('cas.profile.choose.gender')}
          </option>
          <option value="1" selected={gender === 1}>
            {' '}
            {translate('cas.profile.male')}
          </option>
          <option value="2" selected={gender === 2}>
            {' '}
            {translate('cas.profile.female')}
          </option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="transparent-bg mb-3" controlId="birthDate">
        <Form.Label className="small calendar-icon">
          {translate('cas.profile.birthDate')}
        </Form.Label>
        <CasCalendar
          type="Calendar"
          onConfirm={onDateConfirm}
          onLocaleChange={onDateConfirm}
          selectionRange={dateSelect}
          showIcon={false}
          placeholder={translate('cas.profile.choose.birthDate')}
        />
        <div style={{ color: '#fe5000', fontSize: '0.625rem' }}>
          {birthDateError && translate('cas.registration.birthDate.error')}
          {birthDateRequired &&
            translate('cas.registration.birthDate.birthDateRequired')}
        </div>
      </Form.Group>

      <Form.Group className="mb-3" controlId="accountNumber">
        <Form.Label className="small user-icon">
          {translate('cas.registration.accountNumber')}
        </Form.Label>
        <Form.Control
          type="text"
          name="accountNumber"
          placeholder={translate('cas.registration.inputAccountNumber')}
          autoComplete="accountNumber"
          isInvalid={!!state.errors?.accountNumber}
          required
        />
        <Form.Control.Feedback type="invalid">
          {state.errors &&
            state.errors.accountNumber &&
            state.errors.accountNumber[0]}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group
        className="reg-gender-form-group mb-3"
        controlId="vehicleType"
      >
        <Form.Label className="small gender-icon">
          {' '}
          {translate('cas.profile.vehicleType')}
        </Form.Label>
        <Form.Select
          name="vehicleType"
          className="custom-dropdown-register"
          onChange={(e) => {
            setVehicleType(Number.parseInt(e.target.value));
          }}
          isInvalid={state.validated && vehicleType < 0}
        >
          <option value="1" selected={vehicleType === 1}>
            {' '}
            {translate('cas.profile.courier.vehicleType.car')}
          </option>
          <option value="2" selected={vehicleType === 2}>
            {' '}
            {translate('cas.profile.courier.vehicleType.scooter')}
          </option>

          <option value="3" selected={vehicleType === 3}>
            {' '}
            {translate('cas.profile.courier.vehicleType.bycicle')}
          </option>
        </Form.Select>
        <Form.Control.Feedback type="invalid">
          {state.errors &&
            state.errors.vehicleType &&
            state.errors.vehicleType[0]}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group
        className="reg-gender-form-group mb-3"
        controlId="citizenStatus"
      >
        <Form.Label className="small gender-icon">
          {' '}
          {translate('cas.profile.citizenStatus')}
        </Form.Label>
        <Form.Select
          name="citizenStatus"
          className="custom-dropdown-register"
          onChange={(e) => {
            setCitizenStatus(Number.parseInt(e.target.value));
          }}
        >
          <option value="1" selected={citizenStatus === 1}>
            {' '}
            {translate('cas.profile.courier.citizenStatus.physicalPerson')}
          </option>
          <option value="2" selected={citizenStatus === 2}>
            {' '}
            {translate('cas.profile.courier.citizenStatus.smallEnterpreneur')}
          </option>

          <option value="3" selected={citizenStatus === 3}>
            {' '}
            {translate('cas.profile.courier.citizenStatus.microEnterpreneur')}
          </option>
        </Form.Select>
        <Form.Control.Feedback type="invalid">
          {state.errors &&
            state.errors.citizenStatus &&
            state.errors.citizenStatus[0]}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3 position-relative" controlId="password">
        <Form.Label className="small password-icon">
          {translate('cas.registration.pswd')}
        </Form.Label>
        <Form.Control
          type="password"
          name="password"
          placeholder={translate('cas.registration.inputPswd')}
          autoComplete="current-password"
          isInvalid={!!state.errors?.password}
          onFocus={onFocus}
          onChange={onChangePassword}
          required
        />
        <span className="toggle-visible-password" onClick={togglePassword} />
        <Form.Control.Feedback type="invalid">
          {state.errors && state.errors.password && state.errors.password[0]}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group
        className="mb-3 position-relative"
        controlId="repeat-password"
      >
        <Form.Label className="small password-icon">
          {translate('cas.registration.rptPswd')}
        </Form.Label>
        <Form.Control
          type="password"
          name="repeatPassword"
          placeholder={translate('cas.registration.inputPswd')}
          autoComplete="repeat-password"
          isInvalid={!!state.errors?.repeatPassword}
          onFocus={onFocus}
          onChange={onChangeRepeatPassword}
          required
        />
        <span className="toggle-visible-password" onClick={togglePassword} />
        <Form.Control.Feedback type="invalid">
          {state.errors &&
            state.errors.repeatPassword &&
            state.errors.repeatPassword[0]}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3" controlId="terms">
        <Form.Check
          type="checkbox"
          className="checkbox checkbox-primary"
          isInvalid={!!state.errors?.agreement}
          feedback={state.errors?.agreement}
          required
        >
          <Form.Check.Input type="checkbox" name="agreement" required />
          <Form.Check.Label className="small d-flex align-items-center">
            <span>{translate('cas.registration.acceptRulesFirst')}</span>
            <button
              type="button"
              className="link-button ms-2"
              onClick={() => toggleModal(true)}
            >
              {translate('cas.registration.acceptRulesSecond')}
            </button>
          </Form.Check.Label>
          <Form.Control.Feedback type="invalid" style={{ paddingLeft: 5 }}>
            {state.errors &&
              state.errors.agreement &&
              state.errors.agreement[0]}
          </Form.Control.Feedback>
        </Form.Check>
      </Form.Group>

      <Button
        type="submit"
        variant="primary"
        className="w-100 mt-3"
        disabled={birthDateError}
      >
        {translate('cas.registration.register')}
      </Button>
    </Form>
  );
};
